import { useMemberName } from "./operative-name";
import { useCallback, useEffect, useRef, useState } from "react";
const doorOpen =
  import.meta.env.VITE_GRADUATION_VIDEO_URL ||
  "https://goodglows.com/media/secretmissions/door-open-v1.mp4";

/** How long to wait for the video to finish before showing the message. */
const STALL_MS = 15_000;

/** The final debrief for adventure 102. No next-mission event is dispatched:
 * the report is already saved, and the agent chooses when to leave. */
export function MissionGraduation() {
  const { data: name } = useMemberName();
  const [opened, setOpened] = useState(
    () =>
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const stallTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const reveal = useCallback(() => {
    if (stallTimer.current) {
      clearTimeout(stallTimer.current);
      stallTimer.current = null;
    }
    setOpened(true);
  }, []);

  useEffect(() => {
    if (opened) return;
    const video = videoRef.current;
    if (!video) return;

    stallTimer.current = setTimeout(reveal, STALL_MS);

    video.play().catch(() => {
      reveal();
    });

    return () => {
      if (stallTimer.current) {
        clearTimeout(stallTimer.current);
        stallTimer.current = null;
      }
    };
  }, [opened, reveal]);

  return (
    <div className="sm-graduation">
      {!opened && (
        <div className="sm-graduation-door">
          <video
            ref={videoRef}
            src={doorOpen}
            aria-label="Your final mission door opens"
            muted
            playsInline
            preload="metadata"
            onEnded={reveal}
            onError={reveal}
          />
          <button type="button" className="sm-graduation-skip" onClick={reveal}>
            Skip animation
          </button>
        </div>
      )}
      {opened && (
        <main
          className="sm-graduation-message"
          aria-labelledby="sm-graduation-title"
        >
          <h1 id="sm-graduation-title">
            Congratulations, <br />
            {name?.trim() ? `Agent ${name.trim()}` : "Agent"}.
          </h1>
          <p className="sm-graduation-lead">
            You completed seven missions—and made someone’s world a little
            better.
          </p>
          <footer>
            <h2>Ready for your next chapter?</h2>
            <a href="https://ultimatemissions.com/">
              Enter Ultimate Missions <span aria-hidden="true">→</span>
            </a>
          </footer>
        </main>
      )}
      <style>{`
        .sm-graduation{position:fixed;inset:0;z-index:45;overflow-y:auto;overscroll-behavior:contain;background:radial-gradient(ellipse at 50% 0%,#392b12 0%,#100e0a 48%,#050505 100%);color:#f4eddb;font-family:Georgia,serif;padding:max(42px,env(safe-area-inset-top)) 24px max(36px,env(safe-area-inset-bottom))}
        .sm-graduation-door{position:fixed;inset:0;z-index:1;background:#050505;display:flex;align-items:center;justify-content:center}
        .sm-graduation-door video{width:100%;height:100%;object-fit:contain}
        .sm-graduation-message{position:relative;max-width:560px;margin:auto;text-align:center;animation:smGraduationReveal .7s both}
        .sm-graduation-kicker{font-family:monospace;font-size:11px;line-height:1.8;letter-spacing:.16em;text-transform:uppercase;color:#d6a94e}
        .sm-graduation h1{font-size:clamp(34px,8vw,58px);line-height:1.1;margin:24px 0;color:#ecd39a;overflow-wrap:anywhere}
        .sm-graduation p{font-size:17px;line-height:1.65;margin:18px 0;color:#c9c1b0;text-wrap:pretty}
        .sm-graduation .sm-graduation-lead{font-size:21px;color:#f4eddb}
        .sm-graduation footer{margin-top:32px;padding-top:26px;border-top:1px solid #d6a94e45}
        .sm-graduation h2{font-size:25px;line-height:1.25;color:#ecd39a}
        .sm-graduation a{display:block;margin-top:24px;padding:17px 20px;border:1px solid #d6a94e;background:#d6a94e;color:#161107;border-radius:6px;font-family:system-ui,sans-serif;font-size:17px;font-weight:700;text-decoration:none}
        .sm-graduation a:hover{background:#ecd39a}
        .sm-graduation a:focus-visible{outline:3px solid #fff;outline-offset:5px}
        .sm-graduation-skip{position:absolute;bottom:max(24px,env(safe-area-inset-bottom));right:24px;border:1px solid #ffffff60;border-radius:8px;padding:12px 16px;background:#000b;color:#f4eddb;font:14px system-ui;cursor:pointer}
        @keyframes smGraduationReveal{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
        @media(prefers-reduced-motion:reduce){.sm-graduation-message{animation:none}}
      `}</style>
    </div>
  );
}
